/**
 * 페이지 경로를 분석용 고정 키로 변환
 * pathname -> page_key 매핑 테이블
 * 라우트 경로와 페이지 키를 매핑합니다.
 * 동적 파라미터(:id 등)가 있는 경로는 패턴 매칭으로 처리합니다.
 */

/**
 * 페이지 경로를 분석용 고정 키로 변환
 * @param {string} pathname - 페이지 경로
 * @returns {string} 페이지 키 (snake_case)
 */
export const getPageKey = (pathname) => {
  const pathKeyMap = {
    // 홈 및 기본
    '/home': 'home',
    '/login': 'login',
    '/repassword': 'repassword',
    
    // 할인 예약 관련
    '/dc/bookingdiscountList': 'discount_booking_list',
    '/dc/bookingdiscountList/create': 'discount_booking_create',
    '/dc/bookingdiscountrsvps': 'discount_booking_reservations',
    
    // 골프장 정보 관련
    '/common/golfinfo': 'golf_info',
    '/common/golfinfo/modify': 'golf_info_modify',
    '/common/golfinfo/event': 'event_list',
    '/common/golfinfo/event/create': 'event_create',
    '/common/golfinfo/event/detail': 'event_detail',
    '/common/golfacount': 'settlement',
    '/common/logList': 'log_list',
    
    // 연단체 예약 관련
    '/yb/yearBookingList': 'year_booking_list',
    '/yb/yearBookingList/detail': 'year_booking_detail',
    '/yb/yearBookingRoundingList': 'year_booking_rounding_list',
    '/yb/yearBookingRoundingList/detail': 'year_booking_rounding_detail',
    '/yb/yearBookingGroups': 'year_booking_groups',
    '/yb/yearBookingGroups/detail': 'year_booking_groups_detail',
    '/yb/noYearBookingGroups': 'no_year_booking_groups',
    
    // 월단체 예약 관련
    '/eb/eventBookingList': 'monthly_group_booking',
    '/eb/eventBookingList/detail': 'monthly_group_booking_detail',
    '/eb/eventBookingRoundingList': 'monthly_group_rounding',
    '/eb/eventBookingRoundingList/detail': 'monthly_group_rounding_detail',
    
    // ERP 예약 관련
    '/erp/erpBookingList': 'erp_booking_list',
    '/erp/erpBookingList/detail': 'erp_booking_detail',
    
    // 검색
    '/search': 'search_results',
  };

  // 정확한 경로 매칭 우선 (더 구체적인 경로부터 확인)
  const sortedPaths = Object.entries(pathKeyMap).sort((a, b) => b[0].length - a[0].length);
  
  // 1단계: 정확한 경로 매칭
  if (pathKeyMap[pathname]) {
    return pathKeyMap[pathname];
  }
  
  // 2단계: 동적 파라미터가 있는 경로 패턴 매칭
  const patternMap = {
    '/dc/bookingdiscountList/create/:id': 'discount_booking_create',
    '/common/golfinfo/modify/:id': 'golf_info_modify',
    '/common/golfinfo/event/:id': 'event_detail',
    '/yb/yearBookingList/:id': 'year_booking_detail',
    '/yb/yearBookingRoundingList/dummy/:id': 'year_booking_rounding_detail',
    '/yb/yearBookingRoundingList/:id': 'year_booking_rounding_detail',
    '/yb/yearBookingGroups/dummy/:id': 'year_booking_groups_detail',
    '/yb/yearBookingGroups/:id': 'year_booking_groups_detail',
    '/eb/eventBookingList/:id': 'monthly_group_booking_detail',
    '/eb/eventBookingRoundingList/:id': 'monthly_group_rounding_detail',
    '/erp/erpBookingList/:id': 'erp_booking_detail',
  };
  
  // 패턴 매칭 시도
  for (const [pattern, key] of Object.entries(patternMap)) {
    const patternRegex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (patternRegex.test(pathname)) {
      return key;
    }
  }
  
  // 3단계: 경로 시작 부분으로 매칭 (더 구체적인 경로부터)
  for (const [path, key] of sortedPaths) {
    if (pathname.startsWith(path + '/') || pathname === path) {
      return key;
    }
  }

  // 4단계: 기본값 - 경로를 snake_case로 변환
  return pathname
    .replace(/^\//, '')
    .replace(/\//g, '_')
    .replace(/[^a-z0-9_]/gi, '_')
    .toLowerCase() || 'unknown';
};
